import { useToast } from "@/hooks/useToast";
import { useState } from "react";
import { deleteImage, uploadImage } from "@/server/services/imageService";
import { isUserAuthenticated } from "../auth";
import { useTranslation } from "react-i18next";

export interface UseImageUploaderReturn {
  isUploading: boolean;
  blobUrl: string;
  filename: string;
  attachmentId: string;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  deleteFile: () => Promise<void>;
  resetUploader: () => void;
}

export interface UseImageUploaderProps {
  fileSizeLimit?: number;
}

const MB = 1024 * 1024;
const MAX_FILE_SIZE = 5 * MB; // 5MB

export function useImageUploader({
  fileSizeLimit = MAX_FILE_SIZE,
}: UseImageUploaderProps = {}): UseImageUploaderReturn {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [blobUrl, setBlobUrl] = useState<string>("");
  const [filename, setFilename] = useState<string>("");
  const [attachmentId, setAttachmentId] = useState<string>("");

  async function handleFileChange(
    e: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    // extra check if auth is present
    const userFingerPrint = isUserAuthenticated();
    if (!userFingerPrint) {
      return;
    }

    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    // Check file size limit
    if (file.size > fileSizeLimit) {
      toast({
        duration: 1500,
        description: t("fileSizeLimitExceeded", {
          size: (fileSizeLimit / MB).toFixed(2),
        }),
        variant: "destructive",
      });
      return;
    }
    setFilename(file.name);
    // Create a blob URL for the optimistic (preview)
    const url = URL.createObjectURL(file);
    setBlobUrl(url);

    try {
      setIsUploading(true);

      const _attachmentId = await uploadImage(file, file.name);

      if (!_attachmentId) {
        toast({
          duration: 1500,
          description: t("errorUploadingImageNoId"),
          variant: "destructive",
        });
        setBlobUrl("");
        setFilename("");
        return;
      }

      setAttachmentId(_attachmentId);
    } catch (error) {
      toast({
        duration: 1500,
        description:
          error instanceof Error ? error.message : t("errorUploadingImage"),
        variant: "destructive",
      });
      setBlobUrl("");
      setAttachmentId("");
      setFilename("");
    } finally {
      setIsUploading(false);
    }
  }

  async function deleteFile() {
    if (!attachmentId || attachmentId === "") {
      return;
    }
    try {
      setIsUploading(true);

      await deleteImage(attachmentId);

      setBlobUrl("");
      setAttachmentId("");
      toast({
        duration: 1500,
        description: t("fileDeleted"),
        variant: "default",
      });
    } catch (error) {
      toast({
        duration: 1500,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  }
  function resetUploader() {
    setIsUploading(false);
    setBlobUrl("");
    setAttachmentId("");
    setFilename("");
    URL.revokeObjectURL(blobUrl);
  }
  return {
    isUploading,
    blobUrl,
    filename,
    attachmentId,
    handleFileChange,
    deleteFile,
    resetUploader,
  };
}
